import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { Query, useMutation, useQuery, useSubscription } from 'react-apollo';
import { withStyles } from '@material-ui/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Spin from 'components/common/Spin';
import { UPDATE_MILESTONE_FAEXERCISE } from 'graphql/student/milestones';
import { GET_FA_EXERCISES } from 'graphql/fa/students';
import { API } from 'aws-amplify';
import useSnack from 'hooks/useSnack';
import StudentLCCalendar from 'modules/student/lead/Meeting';
import StudentHCCalendar from 'modules/student/head/Meeting';
import FACalendar from 'modules/student/fa_coach/Meeting';
import withConfirm from 'hooks/withConfirm';
import moment from 'moment-timezone';
import TaskItem from './TaskItem';
import FAScheduleModal from '../../student/fa_coach/Meeting';
import { ADD_CREDIT } from 'graphql/credits';
import { LISTEN_STUDENT_CREDITS } from 'graphql/credits';
import { DELETE_MILESTONE_EXERCISES } from 'graphql/admin/students';
import PRODUCTS from 'constant/product-types';

const PRODUCTS_LOOKUP = PRODUCTS.reduce((memo, i) => {
  memo[i.value] = i.label;
  return memo;
}, {});

const styles = (theme) => ({
  root: {
    // marginTop: 50,
    maxWidth: 700,
  },
  milestones: {
    paddingBottom: 50,
  },
  appBar: {
    position: 'relative',
  },
});

const Milestones = ({ classes, student, confirm }) => {
  const [updateTask] = useMutation(UPDATE_MILESTONE_FAEXERCISE);
  const [useCredit] = useMutation(ADD_CREDIT);
  const [deleteExercise] = useMutation(DELETE_MILESTONE_EXERCISES);
  const snack = useSnack();
  const [showModal, toggleModal] = useState(false);
  const [exercises, setexercise] = useState([]);
  const user = useSelector((s) => s.user.user);
  const userId = student.user_id;
  const { financial_aid_settings, guardians } = student;
  const { data } = useSubscription(LISTEN_STUDENT_CREDITS, {
    variables: { userId },
  });
  const fa_total = Math.abs(
    data?.usage?.find((i) => i.credit_type === 'fa')?.available || 0,
  );
  const fa_done = Math.abs(
    data?.usage?.find((i) => i.credit_type === 'fa')?.debit || 0,
  );
  const isCreditAvailable = fa_total > fa_done;

  const cancelMeeting = (task) => {
    return API.del('api', `/calendar/events/${task.response.eventId}`).then(
      () => {
        snack.success('Meeting canceled.');
        const payload = {
          id: task.id,
          input: {
            due_date: null,
            status: null,
            response: null,
          },
        };
        return updateTask({
          variables: payload,
        });
      },
      (e) => snack.error(e.message),
    );
  };

  useEffect(() => {
    const exercisesStatusNull = exercises.filter((f) => f.status === null);
    if (exercisesStatusNull.length > 0) {
      const idsToRemove = exercisesStatusNull.map((m) => m.id);
      if (isCreditAvailable) {
        idsToRemove.pop();
      }
      deleteExercise({
        variables: { deleteExercises: idsToRemove },
      });
    }
  }, [exercises]);

  const onRequestCounseling = (task) => {
    toggleModal({ task });
  };

  const onCancelMeeting = (task) => {
    if (task.response?.eventId) {
      confirm(() => cancelMeeting(task));
    } else {
      snack.info('This meeting cannot be canceled.');
    }
  };

  const renderModalContent = () => {
    if (!showModal) return null;
    const { task } = showModal;
    const type = task.exercise.meeting_type;
    const props = {
      task,
      student: {
        name: `${student.user_first_name} ${student.user_last_name}`,
        email: student.user_email,
        fa_id: student.fa_id,
        hc_id: student.hc_id,
        lc_id: student.lc_id,
      },
      allowCustomSlot: true,
      availablePeriods: {
        start: moment
          .utc()
          .add(1, 'd')
          .format(),
        end: moment
          .utc()
          .add(30, 'd')
          .format(),
      },
      onClose: () => toggleModal(),
      onBookingComplete: (event) => {
        return updateTask({
          variables: {
            id: task.id,
            input: {
              due_date: event.slot.start,
              status: 'inprogress',
              response: {
                sub: user.sub,
                eventId: event.id,
                source: 'google',
              },
            },
          },
        });
      },
    };

    const prefix = 'Book';
    if (type === 'cc') {
      return (
        <StudentLCCalendar {...props} title={`${prefix} Coaching Session`} />
      );
    } else if (type === 'hc') {
      return (
        <StudentHCCalendar {...props} title={`${prefix} Milestone Meeting`} />
      );
    } else if (type === 'fa') {
      return (
        <FACalendar
          {...props}
          title={`${prefix} Financial Aid Meeting`}
          parents={guardians.map(({ guardian }) => ({
            email: guardian.user_email,
            display_name: `${guardian.user_first_name} ${guardian.user_last_name}`,
          }))}
          includeStudent={financial_aid_settings?.involve_child === '1'}
        />
      );
    }
  };

  const onUpdateTask = (id, data) => {
    const { status } = data;
    const shouldUseCredit = status === 'done' || status === 'noshow';
    const payload = {
      id,
      input: data,
    };
    updateTask({
      variables: payload,
    }).catch((e) => snack.error(e.message));
    if (shouldUseCredit) {
      window.analytics.track(`Meeting ${status}`, {
        ...data,
        id,
        product: PRODUCTS_LOOKUP[student.product_type],
      });
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useCredit({
        variables: {
          input: {
            credit_type: 'fa',
            user_id: userId,
            amount: -1,
            description: 'Credit usage',
          },
        },
      });
    }
  };

  return (
    <div>
      <Query
        query={GET_FA_EXERCISES}
        fetchPolicy={'cache-and-network'}
        variables={{ userId }}
      >
        {({ data, loading }) => {
          if (!data) {
            return <Spin spinning linear />;
          }

          const { rows } = data;
          setexercise(rows);

          return (
            <div className={classes.root}>
              <Helmet title="Timeline" />
              <div className={classes.milestones}>
                <Card>
                  <CardContent>
                    {rows.map((i) => (
                      <TaskItem
                        key={i.id}
                        task={i}
                        userId={student.id}
                        studentSub={student.sub}
                        user={user}
                        student={student}
                        onSchedule={onRequestCounseling}
                        onReschedule={onRequestCounseling}
                        onCancelMeeting={onCancelMeeting}
                        onUpdate={onUpdateTask}
                      />
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>
          );
        }}
      </Query>
      {renderModalContent()}
    </div>
  );
};

export default withStyles(styles)(withConfirm(Milestones));
